import { Box, Divider, Tooltip, Typography } from '@mui/material'
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { DynamicTypography } from 'src/components/DynamicTextTypography'
import { ICalendarEvent } from 'src/generated-types/calendarEvent'
import { CalendarEventTypes } from 'src/screens/customer/calendar/calendar.constants'
import { EventDetails } from './GlobalCalendar.style'
import { getFormattedDate } from 'src/utils'

interface EventSegment {
  key: string
  text: string
  variant: 'primary' | 'secondary'
}

export const getEventSegments = (event: ICalendarEvent): EventSegment[] => {
  const locality = event.localityName?.trim()
  if (event.type === CalendarEventTypes.WorkOrder) {
    const segments: EventSegment[] = [{ key: 'ao', text: `#${event.entityId}`, variant: 'primary' }]
    if (locality) segments.push({ key: 'loc', text: locality, variant: 'secondary' })
    const resources = (event.resources ?? []).filter(Boolean)
    if (resources.length) segments.push({ key: 'res', text: resources.join(', '), variant: 'secondary' })
    return segments
  }
  const segments: EventSegment[] = [{ key: 'title', text: event.title, variant: 'primary' }]
  if (locality) segments.push({ key: 'loc', text: locality, variant: 'secondary' })
  return segments
}

interface IEventSegmentsProps {
  event: ICalendarEvent
  maxTextWidth?: number
}

// Inline segment row (• separated), used both in the month tile and the week column.
export const EventSegments = ({ event, maxTextWidth }: IEventSegmentsProps) => {
  const segments = getEventSegments(event)
  return (
    <EventDetails maxTextWidth={maxTextWidth}>
      {segments.map((segment, index) => (
        <Fragment key={segment.key}>
          {index > 0 && (
            <Typography component="span" className="event-separator">
              •
            </Typography>
          )}
          <DynamicTypography
            className={segment.variant === 'primary' ? 'event-title' : 'event-locality'}
            text={segment.text}
            isTooltipDisabled
          />
        </Fragment>
      ))}
    </EventDetails>
  )
}

// Rich content shown on hover: title + description up front, with the key reference fields below.
const EventHoverContent = ({ event }: { event: ICalendarEvent }) => {
  const { t } = useTranslation(['calendar', 'common'])
  const resources = (event.resources ?? []).filter(Boolean)
  return (
    <Box sx={{ p: 0.5, maxWidth: 300 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
        {event.type === CalendarEventTypes.WorkOrder && (
          <Box component="span" sx={{ opacity: 0.6, mr: 0.5 }}>
            #{event.entityId}
          </Box>
        )}
        {event.title}
      </Typography>
      {event.localityName && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
          {t('locality', { ns: 'calendar' })}: {event.localityName}
        </Typography>
      )}
      {resources.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
          {t('resource', { ns: 'calendar' })}: {resources.join(', ')}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
        {t('date', { ns: 'calendar' })}: {getFormattedDate(new Date(event.start))}
        {event.end ? ` to ${getFormattedDate(new Date(event.end))}` : ''}
      </Typography>
      {event.description && (
        <>
          <Divider sx={{ my: 0.75 }} />
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {event.description}
          </Typography>
        </>
      )}
    </Box>
  )
}

// Wraps any event element with the hover preview (title + description). The child must forward a ref,
// which a plain DOM element (span/div) does.
export const EventHover = ({ event, children }: { event: ICalendarEvent; children: React.ReactElement }) => (
  <Tooltip
    title={<EventHoverContent event={event} />}
    arrow
    enterDelay={250}
    enterNextDelay={250}
    placement="top"
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
          border: '1px solid rgba(100, 116, 139, 0.18)',
          borderRadius: 2,
          p: 1,
        },
      },
      arrow: { sx: { color: 'background.paper' } },
    }}
  >
    {children}
  </Tooltip>
)
